import React, { useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react';
import ListingTable from '../ListingTable/ListingTable';
import CompaniesSkillsRequired from '../offlineDriveCompaniesList/CompaniesSkillsRequired';
import { useGetOfflineDrivePositions } from 'src/hooks/offlineDrive.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { useSearchParams } from 'react-router-dom';
import { FilterType } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CandidateFilters';
import { useGetAllSkills } from 'src/hooks/skill.hooks';
import { OfflineDriveCandidateStatus, OfflineDrivePositions, SkillType, TypeOfUsers } from 'types';
import { roleOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/RoleFilterInput';
import { IoClose } from 'react-icons/io5';
import RenderFilters from 'src/components/newRecruiterFlow/campus/campusCandidateListing/RenderFilters';
import { useGetMyBDPocsQuery } from 'src/hooks/user.hooks';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import CompaniesDetails from './CompaniesDetails';
import CompaniesActions from './CompaniesActions';
import { TextInput } from 'flowbite-react';
import { TbSearch } from 'react-icons/tb';
import { debounce } from 'lodash';
import BulkSelectAction from 'src/components/newRecruiterFlow/campus/campusCandidateListing/BulkSelectAction';
import { TablePagination, useMediaQuery } from '@mui/material';
import UseKeyBoardShortCut from 'src/utils/UseKeyBoardShortCut';
import MobileCardForOfflineDrivePosition from '../offlineDriveCandidatesList/offlineDrivePositionMobile/MobileCardForOfflineDrivePosition';
import { attendanceStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveAttendanceStatusFilterInput';
import dayjs from 'dayjs';

const tableHeaders: {
	id: string;
	label: string;
	align?: 'center' | 'left' | 'right' | 'inherit' | 'justify';
	sticky?: boolean;
}[] = [
		{ id: 'company', label: 'Company' },
		{ id: 'salary_offering', label: 'Salary Offering' },
		{ id: 'skills_required', label: 'Skills Required' },
		{ id: 'number_of_openings', label: 'No. of Openings' },
		{ id: 'shortlisted', label: 'Shortlisted' },
		{ id: 'interviewed', label: 'Interviewed' },
		{ id: 'hire_reject', label: 'Hire/Reject' },
		{ id: 'bd_poc', label: 'BD POC' },
		{ id: 'actions', label: 'Actions', sticky: true },
	];

const OfflineDriveCompanies = () => {
	const isMobile = useMediaQuery('(max-width: 1279px)');
	const [tableRows, setTableRows] = useState<{ id: string; tableDataJsx: React.ReactNode }[][]>([]);
	const { selectedDrive, bulkSelectIds, setBulkSelectIds } = useUserContext();
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(10);
	const [searchParams, setSearchParams] = useSearchParams();
	const [showFilterDrawer, setShowFilterDrawer] = useState(false);
	const { data: skillsData } = useGetAllSkills({ type: SkillType.SKILL });
	const skillNameMap = new Map(skillsData?.skills?.map(skill => [skill._id, skill.name.replaceAll('_', ' ')]));
	const bulkSelect = searchParams.get('bulkSelect') === 'true';

	UseKeyBoardShortCut(
		'#search-positions',
		{ metaKey: true, key: 'k' },
		(element: HTMLElement | Document | React.RefObject<HTMLElement>) => {
			if (element instanceof HTMLElement) {
				element.focus();
			}
		},
	);

	const { data: offlineDrivePositionData, isLoading } = useGetOfflineDrivePositions({
		driveId: selectedDrive?.value || '',
		page: page,
		limit: limit,
		filters: {
			sortBy: 'bestMatch',
			search: searchParams.get('search') || '',
			roles: JSON.parse(searchParams.get('role') || '[]'),
			skills: JSON.parse(searchParams.get('skills') || '[]'),
			salary: JSON.parse(searchParams.get('salary') || '[]'),
			bdPoc: JSON.parse(searchParams.get('bdPoc') || '[]'),
			attendanceStatus: JSON.parse(searchParams.get('attendanceStatus') || '[]'),
		},
	});

	useEffect(() => {
		setPage(0);
	}, [selectedDrive?.value]);

	const { data: myBDPOCsData } = useGetMyBDPocsQuery();
	const bdOptions = myBDPOCsData?.bds?.map(bd => ({ label: `${bd.name} - ${bd.email}`, value: bd._id }));

	useEffect(() => {
		const allTableRows: {
			id: string;
			tableDataJsx: React.ReactNode;
		}[][] =
			offlineDrivePositionData?.positions?.map((position, index) => [
				{
					id: position?.company?._id?.toString() || '',
					tableDataJsx: (
						<CompaniesDetails
							companyName={position?.company?.name || ''}
							role={position?.title}
							logoUrl={position?.company?.logo || ''}
							companyUrl={position?.company?.url ? position?.company?.url : ''}
							offlineDriveRoom={position?.company?.offlineDriveRoom}
							isCompanyPresent={position?.company?.offlineDriveCompanyStatus === OfflineDriveCandidateStatus.PRESENT}
							positionLabel={position?.positionLabel}
							attendDriveDate={position?.company?.offlineDriveCompanyStatusHistory?.sort(
								(a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix()
							)[0]?.updatedAt}
						/>
					),
				},
				{
					id: 'salary_offering',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							<p className="text-base font-[#28293D] font-medium">
								{`${position?.salary?.min}-${position?.salary?.max}LPA`}
							</p>
						</div>
					),
				},
				{
					id: 'skills_required',
					tableDataJsx: <CompaniesSkillsRequired skillsRequired={position?.skills} />,
				},
				{
					id: 'number_of_openings',
					tableDataJsx: <div className="flex items-center justify-center">{position?.vacancy}</div>,
				},
				{
					id: 'shortlisted',
					tableDataJsx: <div className="flex items-center justify-center">{position?.shortlistedCount}</div>,
				},
				{
					id: 'interviewed',
					tableDataJsx: <div className="flex items-center justify-center">{position?.interviewedCount}</div>,
				},
				{
					id: 'hire_reject',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							{position?.hiredCount}/{position?.rejectedCount}
						</div>
					),
				},
				{
					id: 'bd_poc',
					tableDataJsx: (
						<div className="flex grow-2 gap-2 items-center justify-center text-center">{position?.bdPoc?.name}</div>
					),
				},
				{
					id: 'actions',
					tableDataJsx: (
						<div className="flex grow-2 items-center justify-center">
							<CompaniesActions
								company={position?.company}
								companyId={position?.company?._id || ''}
								companyName={position?.company?.name || ''}
								driveId={selectedDrive?.value || ''}
								positionId={position?._id?.toString() || ''}
							/>
						</div>
					),
				},
			]) || [];
		setTableRows(allTableRows);
	}, [offlineDrivePositionData]);

	useEffect(() => {
		if (offlineDrivePositionData) {
			const { positions } = offlineDrivePositionData;
		}
	}, [offlineDrivePositionData]);

	const [appliedFilters, setAppliedFilters] = useState<FilterType>([]);

	useEffect(() => {
		const filters: FilterType = [];

		// Get skill filter
		const skill = JSON.parse(searchParams.get('skills') || '[]');
		if (skill.length) {
			filters.push({
				filterKey: 'skills',
				data: skill.map((id: string) => ({
					label: skillNameMap.get(id) || id, // Use skill name or ID if not found
					value: id,
				})),
			});
		}

		// Get salary filter
		const salary = JSON.parse(searchParams.get('salary') || '[]');
		if (salary?.length) {
			filters.push({
				filterKey: 'salary',
				data: salary.map((el: string) => ({ label: `${el?.split('_')[0]} to ${el?.split('_')[1]} LPA`, value: el })),
			});
		}

		// Get role filter
		const role = JSON.parse(searchParams.get('role') || '[]');
		if (role.length) {
			filters.push({
				filterKey: 'role',
				data: roleOptions?.filter(option => role?.includes(option?.value)),
			});
		}

		const bdPoc = JSON.parse(searchParams.get('bdPoc') || '[]');
		if (bdPoc.length) {
			filters.push({
				filterKey: 'bdPoc',
				data: bdPoc.map((id: string) => ({
					label: bdOptions?.find(c => c.value === id)?.label || id, // Use campus name or ID if not found
					value: id,
				})),
			});
		}

		const attendanceStatus = JSON.parse(searchParams.get('attendanceStatus') || '[]');
		if (attendanceStatus.length) {
			filters.push({
				filterKey: 'attendanceStatus',
				data: attendanceStatusOptions.filter(option => attendanceStatus?.includes(option?.value)),
			});
		}

		setAppliedFilters(filters);

		const bulkSelect = searchParams.get('bulkSelect') === 'true';

		// Check if any other search parameters have changed
		const otherParamsChanged = [
			'availability',
			'location',
			'skills',
			'salary',
			'role',
			'education',
			'campus',
			'search',
			'bdPoc',
			'attendanceStatus',
		].some(param => searchParams.has(param));

		if (!bulkSelect && otherParamsChanged) {
			setPage(0);
		}
	}, [searchParams, skillsData]);

	const removeFilter = (filterKey: string, value?: string) => {
		if (value) {
			const currentValues = JSON.parse(searchParams.get(filterKey) || '[]');
			const updatedValues = currentValues.filter((v: string) => v !== value);
			if (updatedValues.length > 0) {
				searchParams.set(filterKey, JSON.stringify(updatedValues));
			} else {
				searchParams.delete(filterKey);
			}
			setSearchParams(searchParams);
		}
	};

	const onSelectAllCompanies = () => {
		if (offlineDrivePositionData?.positions?.length === bulkSelectIds.length) {
			setBulkSelectIds([]);
		} else {
			const companyIds = offlineDrivePositionData?.positions?.map(position => position?.company?._id?.toString()) || [];
			setBulkSelectIds(companyIds);
		}
	};

	const changeHandler = (search: string) => {
		setPage(0);
		searchParams.set('search', search);
		setSearchParams(searchParams);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);

	const FilterComponent = () => (
		<RenderFilters
			showCampusFilter={false}
			showLocationFilter={false}
			showEducationFilter={false}
			showAvailabilityFilter={false}
			showMatchedPercentileFilter={false}
			showRoleFilter={true}
			showSkillFilter={true}
			showMatchFilter={false}
			showStatusFilter={false}
			showSalaryFilter={true}
			showBdPocFilter={true}
			showAttendanceStatusFilter={true}
			appliedFilters={appliedFilters}
			setAppliedFilters={setAppliedFilters}
			showFilterDrawer={showFilterDrawer}
			setShowFilterDrawer={setShowFilterDrawer}
			filterName={isMobile ? '' : 'Filters'}
			filterOrder={['role', 'skill', 'bdPoc', 'salary', 'attendanceStatus']}
		/>
	);

	return (
		<div className="py-4 md:py-[30px] max-w-[1440px] px-4 xl:px-10 mx-auto">
			<div className="text-base md:text-xl font-[600] text-[#1C1C28] pb-4 md:pb-[20px] border-b border-[#E4E4EB] mb-4">
				Opportunities ({offlineDrivePositionData?.pagination?.totalItems || 0})
			</div>

			<div className="flex items-center flex-wrap gap-3">
				<TextInput
					id="search-positions"
					placeholder="Search company by name..."
					className="w-full max-w-[731px] [&_input]:bg-white flex-1 md:flex-none"
					icon={() => <TbSearch color="#C7C9D9" />}
					onChange={e => {
						debouncedChangeHandler(e.target.value);
					}}
				/>
				<div>
					<BulkSelectAction
						bulkSelectedIds={bulkSelectIds}
						setBulkSelectedIds={setBulkSelectIds}
						typeOfUsers={'offline-drive-positions'}
					/>
				</div>

				{isMobile && <div className="xl:hidden">{FilterComponent()}</div>}
			</div>

			<div className="py-2 xl:py-4">
				{!isMobile && <div className="hidden xl:block">{FilterComponent()}</div>}

				{appliedFilters.length > 0 && (
					<div className="mt-[12px] flex-wrap gap-4 hidden xl:flex">
						{appliedFilters
							?.filter(el => el?.data?.length)
							.map(filter => (
								<div key={filter.filterKey} className="flex flex-wrap items-center gap-4">
									{filter.data.map((value, index) => (
										<div
											key={index}
											className="text-sm font-[500] flex gap-1 bg-[#E4E4EB] text-[#555770] pl-[6px] rounded-[4px]"
										>
											<span className="py-[2px] max-w-[30ch] truncate">{value?.label}</span>
											<button
												onClick={() => removeFilter(filter.filterKey, value?.value)}
												className="hover:bg-gray-300 active:bg-gray-400 px-[6px] rounded-r-[4px]"
											>
												<IoClose className="w-[12px] h-[12px]" />
											</button>
										</div>
									))}
								</div>
							))}
					</div>
				)}
			</div>

			{isLoading ? (
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="md:!w-[35%] mx-auto" />
			) : (
				<>
					{!isMobile ? (
						<div className="w-full">
							{selectedDrive && (
								<ListingTable
									page={page}
									setPage={setPage}
									showPagination={true}
									tableRows={tableRows}
									setRowsPerPage={setLimit}
									tableHeaders={tableHeaders}
									showBulkSelectAction={bulkSelect}
									onSelectAllCandidates={onSelectAllCompanies}
									count={offlineDrivePositionData?.pagination?.totalItems || 0}
									rowsPerPage={offlineDrivePositionData?.pagination?.itemsPerPage || 10}
								/>
							)}
						</div>
					) : (
						<div>
							<div className="flex flex-col gap-2">
								{offlineDrivePositionData?.positions.map((position: OfflineDrivePositions) => {
									return (
										<div>
											<MobileCardForOfflineDrivePosition position={position} bulkSelect={bulkSelect} attendDriveDate={position?.company?.offlineDriveCompanyStatusHistory?.sort(
												(a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix()
											)[0]?.updatedAt} />
										</div>
									);
								})}
							</div>
							<div className="flex justify-center items-center w-full mt-4">
								<TablePagination
									sx={{
										'&': {
											background: '#FAFAFC',
											'[data-mode="dark"] &': {
												background: '#18222D',
											},
										},
									}}
									rowsPerPageOptions={[5, 10, 25, 100]}
									component="div"
									count={offlineDrivePositionData?.pagination?.totalItems || 0}
									rowsPerPage={offlineDrivePositionData?.pagination?.itemsPerPage || 10}
									page={page}
									onPageChange={(event: unknown, newPage: number) => {
										setPage && setPage(newPage);
									}}
									onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setLimit(parseInt(event.target.value, 10));
										setPage && setPage(0);
									}}
								/>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};
export default OfflineDriveCompanies;
